/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Slobodan Djordjevic
 */
import React, { useEffect, useState } from "react"
import { useHistory, useLocation } from "react-router"
import { useParams } from "react-router-dom"
import { SimpleTopBar } from "../../SimpleTopBar"
import { Element, Column, Row, Label, Color, TRCInput, Button } from "trux"
import { fetchUser, sendReferral } from "../actions"
import { validateEmail, validatePhone } from "../../../util/helpers"
import referralsTraceBot from "../../../assets/img/referrals_tracebot.svg"

import "./ReferralLandingPage.css"

const TEXT_GENERAL = `Want personalized playlists and stats delivered fast?`
const EXPLANATION_GENERAL = `Trace captures your games, generates tactical playlists, and delivers each player's highlights directly to their inboxes after each game.`
const EXPLANATION_PERSONAL = `Type in your email or phone number and Trace will send you more info.`
const BUTTON_CONTENT_GENERAL = `Get more info`
const BUTTON_CONTENT_PERSONAL = `Send me info about Trace`

export const ReferralsLandingPage = () => {
    const history = useHistory()
    const location = useLocation()
    let { user_id } = useParams()
    let [referralInfo, setReferralInfo] = useState("")
    let [loadingUserInfo, setLoadingUserInfo] = useState(false)
    let [text, setText] = useState(TEXT_GENERAL)
    let [explanation, setExplanation] = useState(EXPLANATION_GENERAL)
    let [buttonContent, setButtonContent] = useState(BUTTON_CONTENT_GENERAL)
    let [hasError, setHasError] = useState(false)

    let source = location.pathname.split("/")[2]

    useEffect(() => {
        if (user_id) {
            setLoadingUserInfo(true)

            fetchUser(user_id).then((user) => {
                if (user) {
                    let namePart = user.first_name
                        ? " it’s " + user.first_name + ","
                        : ""
                    let teamPart = user.team_title
                        ? "from " + user.team_title + ","
                        : ""
                    let personalizedText = `Hey${namePart} ${teamPart} we talked about Trace on the field.`
                    setText(personalizedText)
                    setExplanation(EXPLANATION_PERSONAL)
                    setButtonContent(BUTTON_CONTENT_PERSONAL)
                }
                setLoadingUserInfo(false)
            })
        }
    }, [])

    const submitReferral = () => {
        if (validateEmail(referralInfo)) {
            sendReferral(source, `mailto:${referralInfo}`, user_id)
            setHasError(false)
            redirectToSuccess()
        } else if (validatePhone(referralInfo)) {
            sendReferral(source, `tel:${referralInfo}`, user_id)
            setHasError(false)
            redirectToSuccess()
        } else {
            setHasError(true)
        }
    }

    const redirectToSuccess = () => {
        history.push({
            pathname: "/ref/referral-success",
        })
    }

    return (
        <>
            <SimpleTopBar />

            <Element className="referrals-content-wrapper">
                <Column
                    className="referrals-content"
                    justifyContent="center"
                    marginLeft="4"
                    marginTop="10"
                    marginRight="4"
                    marginBottom="8"
                >
                    <Row justifyContent="center" marginBottom="4">
                        <Element
                            className="referrals-trace-bot"
                            tag="img"
                            src={referralsTraceBot}
                            alt="referrals-trace-bot"
                        />
                    </Row>
                    <Row justifyContent="center" margin="4">
                        {!loadingUserInfo && (
                            <Label
                                className="referrals-text"
                                type={Label.Type.Heading2}
                                color={Color.Black}
                                content={text}
                            />
                        )}
                    </Row>
                    <Row
                        justifyContent="center"
                        marginLeft="4"
                        marginRight="4"
                        marginBottom="8"
                    >
                        <Label
                            className="referrals-text"
                            type={Label.Type.Body}
                            color={Color.Grey1}
                            content={explanation}
                        />
                    </Row>
                    <TRCInput
                        id="emailOrPhone"
                        className={`email-or-phone ${
                            hasError ? "invalid" : ""
                        }`}
                        value={referralInfo}
                        onChange={(info) => setReferralInfo(info)}
                        placeholder={"Email or phone number"}
                    />
                    {hasError && (
                        <Label
                            className="error-message"
                            type={Label.Type.SmallBody}
                            color={Color.Error}
                            content="Please enter valid email or phone number"
                        />
                    )}
                    {!loadingUserInfo && (
                        <Button
                            primary
                            className="send-me-info-button"
                            height={56}
                            content={buttonContent}
                            marginTop="4"
                            small
                            onClick={submitReferral}
                            trkAction="Send Info"
                        />
                    )}
                </Column>
            </Element>
        </>
    )
}
